import { useCallback, useContext, useMemo } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'

// LuxKit Components
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import Caption from 'LuxKit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import Places from 'constants/places'
import Clickable from 'components/Common/Clickable'
import focusStyle from 'styles/utils/focusStyle'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import clsx from 'clsx'
import { useAppSelector } from 'hooks/reduxHooks'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import getOfferListKey from 'lib/offer/offerListKey'
import { getEffectiveFlightPrice, getFlightSearchKey } from 'lib/flights/flightUtils'
import { buildFlightSearchPayload, buildBundleOfferFilters } from '../MaldivesDirectUtils'
import { HOTEL_OFFERS_LIMIT, MALDIVES_BUNDLE_DEFAULT_NIGHTS, MALDIVES_CHARTER_CONFIG } from 'constants/charterFlight'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { FlightsClassTypes, type FareClassWithReward } from 'constants/flight'
import CurrencyContext from 'contexts/currencyContext'
import type { CalendarItemBundleData } from '../hooks/types'
import type { MaldivesCabinHotelEntry } from '../hooks/useMaldivesOffersPricing'
import config from 'constants/config'
import { LUXURY_ESCAPES } from 'constants/brands'
import { getLowestAvailableHotelPrice, useSeatEligiblePassengers } from '../hooks/utils'
import useAllCharterFlightJourneys from 'hooks/CharterFlights/useAllCharterFlightJourneys'
import useCharterRewardSeatsIndicatorForDate from '../hooks/useCharterRewardSeatsIndicatorForDate'
import useFeatureFlag from 'hooks/Optimizely/useFeatureFlag'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import {
  CharterRewardAvailabilityContext,
  type CabinDateJourneys,
} from '../hooks/charterRewardAvailabilityContext'
import { CHARTER_CASH_CABINS } from 'lib/maldivesCharter/charterCabins'
import { aggregateMaldivesCharterMonthTileRewardPricing } from './maldivesCharterMonthTileRewardPricing'
import type { CharterRewardLeg } from 'hooks/Flights/useCharterRewardAvailability'
import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import ClassicRewardsSealIcon from 'LuxKit/Icons/loyalty/ClassicRewardsSealIcon'
import { charterCalendarDayKey } from 'lib/maldivesCharter/charterCalendarDayKey'
import type { DateItem } from './MaldivesTripOptionsList'
import MaldivesCharterTripOptionsTable from './MaldivesCharterTripOptionsTable'
import { trimWhere } from 'lib/array/arrayUtils'
import TextLink from 'LuxKit/TextLink'
import { buildSearchParamsForFilterPane } from 'components/Pages/DestinationLandingPage/DestinationLandingPageUtils'
import { INTL_SHORT_MONTH } from 'constants/dateFormats'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'

// Types
interface DateRange {
  departureDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  durationDays: number // Total days from departureDate to returnDate
  nights: number // Nights from checkIn to checkOut
}

interface TripOption {
  id: string
  departDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  price: number
  memberPrice?: number
  currency: string
  seatsLeft?: number
  nights: number
  available: boolean
  month?: string
  fareClass?: FareClassWithReward
}

const ScrollableContainer = styled(VerticalSpacer)`
  width: 100%;
  min-width: 0;
`

// Styled Components - Improved Month Selector (inspired by TourV2)
const MonthTile = styled(Clickable)`
  position: relative;
  padding: ${rem(8)} ${rem(12)};
  text-align: center;
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  min-height: ${rem(110)};
  min-width: ${rem(104)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  transition: border-color 0.2s, background-color 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${rem(2)};

  &:focus-visible {
    ${focusStyle}
  }

  ${mediaHoverable} {
    &:hover {
      background-color: ${props => props.theme.palette.neutral.default.seven};
    }
  }

  &.selected {
    border: 1px solid ${props => props.theme.palette.neutral.default.one};
  }
`

const StyledCardCarousel = styled(CardCarousel)`
  margin-top: ${rem(16)};
`

const TripOptionsContainer = styled(Group)`
  width: 100%;
  margin-top: ${rem(20)};
`

const CalendarContainer = styled(VerticalSpacer)`
  background-color: ${props => props.theme.palette.neutral.default.seven};
  padding: ${rem(20)} ${rem(20)} ${rem(8)};
  margin: 0 ${rem(-24)} ${rem(-24)};
`

const BottomLinkContainer = styled.div`
  padding: ${rem(20)};
  display: flex;
  justify-content: center;
`

interface Props {
  onDateSelect: (option: TripOption) => void
  selectedOptionId?: string
  nights?: number
  occupancies?: Array<App.Occupants>
  fareClass: FlightsClassTypes
  /** Full selected fare class including reward variant — used for the "Selected" indicator in the trip table. */
  selectedFareClass?: FareClassWithReward
  priceMode: 'total' | 'perPerson'
  // LuxPlus related
  luxPlusEnabled?: boolean
  dateRangesByMonth: Record<string, Array<DateRange>>
  selectedMonth: string
  onMonthChange: (month: string) => void
  /** Ready-to-use calendar item data - no calculation needed **/
  itemBundleDataByDate?: Record<string, CalendarItemBundleData>
  /** Per-cabin lowest hotel by date so each per-cabin cell in the trip table can use
   *  the hotel min for offers that allow that cabin (independent of page selection). */
  hotelPriceByCabinAndDate?: Partial<Record<FlightsClassTypes, Record<string, MaldivesCabinHotelEntry>>>
  mode: 'search' | 'offer'
  /** When mode is 'offer', pass the offer to show Kids Fly Free label only when offer has campaign tag */
  offer?: App.HotelOffer
  /** When true, hide dates and trip-table rows that don't have reward-seat availability for every leg. */
  rewardSeatsOnly?: boolean
}

function MaldivesDirectSearchFlightCalendar(props: Props) {
  const {
    onDateSelect,
    selectedOptionId,
    nights = MALDIVES_BUNDLE_DEFAULT_NIGHTS,
    occupancies,
    fareClass,
    priceMode,
    luxPlusEnabled: luxPlusEnabledProp,
    dateRangesByMonth,
    selectedMonth,
    onMonthChange,
    mode,
    itemBundleDataByDate,
    hotelPriceByCabinAndDate,
    rewardSeatsOnly = false,
  } = props

  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()

  const luxPlusEnabled = Boolean(luxPlusEnabledProp && config.BRAND === LUXURY_ESCAPES)

  const allOfferLists = useAppSelector(state => state.offer.offerLists)
  const allOffers = useAppSelector(state => state.offer.offers)
  const allFlights = useAppSelector(state => state.flights.searchV2Flights)
  const allOfferMetaData = useAppSelector(state => state.offer.searchResultMetadata.offerMetaData)

  // Calculate total passengers for bundle pricing
  const totalPassengers = useMemo(
    () => sumUpOccupancies(occupancies),
    [occupancies],
  )
  const seatEligiblePassengers = useSeatEligiblePassengers(occupancies ?? [{ adults: 2, children: 0 }])

  const effectiveOccupancies = occupancies ?? [{ adults: 2, children: 0 }]

  // Fetch one journey schedule per cash cabin so each cash column shows its own price.
  // Calls are cached at the Redux level (per cabin's flightSearchKey), so calling N times
  // with the same args is fine — the second mount just reads from the existing slice.
  // CHARTER_CASH_CABINS is a stable 3-tuple [ECONOMY, PREMIUM, BUSINESS]; explicit calls
  // keep us within the rules of hooks.
  const economyJourneys = useAllCharterFlightJourneys({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    occupancies: effectiveOccupancies,
    fareClass: FlightsClassTypes.ECONOMY,
  })
  const premiumJourneys = useAllCharterFlightJourneys({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    occupancies: effectiveOccupancies,
    fareClass: FlightsClassTypes.PREMIUM,
  })
  const businessJourneys = useAllCharterFlightJourneys({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    occupancies: effectiveOccupancies,
    fareClass: FlightsClassTypes.BUSINESS,
  })

  // Per-cabin per-date journey lookup. Date key is `checkIn` (matches outbound arrival;
  // inbound departure matches `checkOut` for the same row).
  const journeysByCabinDate = useMemo(() => {
    const map = new Map<FlightsClassTypes, Map<string, CabinDateJourneys>>()
    const cabinFares: Array<[FlightsClassTypes, ReturnType<typeof useAllCharterFlightJourneys>]> = [
      [FlightsClassTypes.ECONOMY, economyJourneys],
      [FlightsClassTypes.PREMIUM, premiumJourneys],
      [FlightsClassTypes.BUSINESS, businessJourneys],
    ]
    cabinFares.forEach(([cabin, fares]) => {
      const cabinMap = new Map<string, CabinDateJourneys>()
      const departingJourneys = fares?.fares?.[0] ?? []
      const returningJourneys = fares?.fares?.[1] ?? []
      Object.values(dateRangesByMonth).forEach((dateRanges) => {
        dateRanges.forEach(({ checkIn, checkOut }) => {
          const departing = departingJourneys.find((journey) => {
            const arrival = charterCalendarDayKey(journey.flightGroup.flights.at(-1)?.arrivalDate)
            return arrival === checkIn
          })
          const returning = returningJourneys.find((journey) => {
            const departure = charterCalendarDayKey(journey.flightGroup.flights.at(0)?.departingDate)
            return departure === checkOut
          })
          cabinMap.set(checkIn, { departing, returning })
        })
      })
      map.set(cabin, cabinMap)
    })
    return map
  }, [economyJourneys, premiumJourneys, businessJourneys, dateRangesByMonth])

  const getJourneysForCabinDate = useCallback(
    (cabin: FlightsClassTypes, dateKey: string): CabinDateJourneys | undefined => {
      return journeysByCabinDate.get(cabin)?.get(dateKey)
    },
    [journeysByCabinDate],
  )

  const { rewardLegs, rewardLegsByDate } = useMemo(() => {
    // Reward indicator coverage: include every cabin's journey_keys in the flat `legs`
    // array (deduped) so per-cell `getLegResult(journeyKey)` works for any cabin's leg.
    // `legsByDate` is intentionally kept to the page's selected cabin only — the existing
    // per-date all-or-nothing `isAvailableForDate` semantics drive the month-tile seal,
    // and broadening to "any cabin available" would change that meaning.
    const legs: Array<CharterRewardLeg> = []
    const legsByDate: Record<string, Array<string>> = {}
    const seenJourneyKeys = new Set<string>()

    const pageCabin = CHARTER_CASH_CABINS.includes(fareClass) ? fareClass : FlightsClassTypes.ECONOMY
    const pageCabinMap = journeysByCabinDate.get(pageCabin)

    Object.values(dateRangesByMonth).forEach((dateRanges) => {
      dateRanges.forEach(({ checkIn }) => {
        const pageEntry = pageCabinMap?.get(checkIn)
        if (pageEntry?.departing?.journeyKey && pageEntry.returning?.journeyKey) {
          legsByDate[checkIn] = [pageEntry.departing.journeyKey, pageEntry.returning.journeyKey]
        }

        // Flatten across all cabins for the indicator's leg coverage.
        CHARTER_CASH_CABINS.forEach((cabin) => {
          const entry = journeysByCabinDate.get(cabin)?.get(checkIn)
          for (const journey of [entry?.departing, entry?.returning]) {
            const key = journey?.journeyKey
            if (!key || seenJourneyKeys.has(key)) continue
            seenJourneyKeys.add(key)
            legs.push({ journeyKey: key, legTotal: journey.price.all.totalFare ?? 0 })
          }
        })
      })
    })

    return { rewardLegs: legs, rewardLegsByDate: legsByDate }
  }, [journeysByCabinDate, dateRangesByMonth, fareClass])

  const isRewardSeatsEnabled = useFeatureFlag(OptimizelyFeatureFlags.maldivesCharterRewardSeats)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const rewardIndicator = useCharterRewardSeatsIndicatorForDate({
    legs: rewardLegs,
    legsByDate: rewardLegsByDate,
    passengerCount: seatEligiblePassengers,
    disabled: !isRewardSeatsEnabled || !hasLoyaltyAccount || rewardLegs.length === 0,
  })

  const charterRewardContextValue = useMemo(() => ({
    ...rewardIndicator,
    getJourneysForCabinDate,
  }), [rewardIndicator, getJourneysForCabinDate])

  const monthRewardSeals = useMemo(() => {
    // Month-tile seal lights up whenever ANY cabin has both legs reward-available
    // for at least one date in the month, independent of the page's selected cabin —
    // so users discover reward inventory before picking a fare class.
    const datesByMonth: Record<string, Array<{ dateKey: string; allLegsAvailable: boolean }>> = {}
    Object.entries(dateRangesByMonth).forEach(([monthName, dateRanges]) => {
      datesByMonth[monthName] = dateRanges.map(({ checkIn }) => {
        const anyCabinAvailable = CHARTER_CASH_CABINS.some((cabin) => {
          const entry = journeysByCabinDate.get(cabin)?.get(checkIn)
          const depKey = entry?.departing?.journeyKey
          const retKey = entry?.returning?.journeyKey
          if (!depKey || !retKey) return false
          return rewardIndicator.getLegResult(depKey)?.available === true
            && rewardIndicator.getLegResult(retKey)?.available === true
        })
        return { dateKey: checkIn, allLegsAvailable: anyCabinAvailable }
      })
    })
    return aggregateMaldivesCharterMonthTileRewardPricing({ datesByMonth })
  }, [dateRangesByMonth, journeysByCabinDate, rewardIndicator])

  const currencyContext = useContext(CurrencyContext)

  // Generate months for the selector with bundle pricing
  const months = useMemo(() => {
    const monthNames: Array<{
      name: string
      price: number
      fetching: boolean
      monthLabel: string
      year: number
      unavailable: boolean
    }> = []

    Object.entries(dateRangesByMonth).forEach(([monthName, dateRanges]) => {
      // Skip months with no date entries
      if (dateRanges.length === 0) return

      // Calculate lowest bundle price per person for this month
      let lowestPrice = 0
      let allResolved = true

      dateRanges.forEach(({ departureDate, returnDate, checkIn, checkOut }) => {
        if (itemBundleDataByDate) {
          const itemData = itemBundleDataByDate[checkIn]

          // Consider unresolved if item is still fetching
          if (itemData?.fetching) {
            allResolved = false
          }

          // Calculate bundle price based on priceMode and luxPlusEnabled
          const bundlePrice = priceMode === 'total'
            ? (luxPlusEnabled ? (itemData?.memberTotalPrice || itemData?.totalPrice) : itemData?.totalPrice)
            : (luxPlusEnabled ? (itemData?.memberPricePerPerson || itemData?.pricePerPerson) : itemData?.pricePerPerson)

          if (itemData?.available && bundlePrice && bundlePrice > 0) {
            if (lowestPrice === 0 || bundlePrice < lowestPrice) {
              lowestPrice = bundlePrice
            }
          }
        } else if (mode === 'search') {
          // Use checkIn/checkOut (inner dates) for hotel queries
          const hotelFilters: App.OfferListFilters = buildBundleOfferFilters({
            destinationId: Places.Maldives.id,
            destinationName: Places.Maldives.name,
            checkIn: checkIn,
            checkOut: checkOut,
            rooms: occupancies || [{ adults: 2, children: 0 }],
            limit: HOTEL_OFFERS_LIMIT,
          })

          // Use departureDate/returnDate (outer dates) for flight queries
          const flightParams = buildFlightSearchPayload({
            departureAirport: MALDIVES_CHARTER_CONFIG.DEPARTURE_AIRPORT,
            arrivalAirport: MALDIVES_CHARTER_CONFIG.ARRIVAL_AIRPORT,
            departureDate: departureDate,
            returnDate: returnDate,
            occupancies: occupancies || [{ adults: 2, children: 0 }],
            fareClass: fareClass,
            currency: currencyContext || 'AUD',
          })

          const hotelKey = getOfferListKey(hotelFilters)
          const flightKey = getFlightSearchKey(flightParams)

          const offerList = allOfferLists[hotelKey]
          const flightData = allFlights[flightKey]

          // Calculate hotel price - only from available offers with valid capacity
          const offerIds = offerList?.offerIds || []
          const offerMetaDataForList = allOfferMetaData[hotelKey]
          const rooms = occupancies || [{ adults: 2, children: 0 }]

          const lowestHotelPrice = getLowestAvailableHotelPrice({
            offerIds,
            allOffers,
            offerMetaData: offerMetaDataForList,
            rooms,
            nights,
            luxPlusEnabled,
          })

          // Calculate flight price
          const departingFlight = flightData?.fares?.[0]?.[0]
          const returningFlight = flightData?.fares?.[1]?.[0]
          const departingFare = departingFlight ? getEffectiveFlightPrice(departingFlight, seatEligiblePassengers).all.totalFare ?? 0 : 0
          const returningFare = returningFlight ? getEffectiveFlightPrice(returningFlight, seatEligiblePassengers).all.totalFare ?? 0 : 0
          const totalFlightFare = departingFare + returningFare
          const hotelResolved = !!offerList && !offerList.fetching && !!offerMetaDataForList
          const flightResolved = !!flightData && !flightData.fetching && (totalFlightFare > 0)
          const resolvedThisRange = hotelResolved && flightResolved
          if (!resolvedThisRange) {
            allResolved = false
          }

          // Calculate bundle price based on price mode
          if (resolvedThisRange && lowestHotelPrice > 0 && totalFlightFare > 0) {
            const bundleTotal = lowestHotelPrice + totalFlightFare
            const bundlePrice = priceMode === 'perPerson' && totalPassengers > 0
              ? bundleTotal / totalPassengers
              : bundleTotal

            if (bundlePrice > 0 && (lowestPrice === 0 || bundlePrice < lowestPrice)) {
              lowestPrice = bundlePrice
            }
          }
        }
      })

      // Extract month and year for display
      const firstDate = dateRanges[0]?.checkIn
      const date = moment(firstDate)

      monthNames.push({
        name: monthName,
        price: lowestPrice || 0,
        fetching: !allResolved,
        monthLabel: dateFormatter(date, INTL_SHORT_MONTH),
        year: date.year(),
        unavailable: allResolved && !lowestPrice,
      })
    })

    return trimWhere(monthNames, m => m.unavailable)
  }, [dateFormatter, dateRangesByMonth, nights, luxPlusEnabled, totalPassengers, seatEligiblePassengers, allOfferLists, allOffers, allFlights, allOfferMetaData, fareClass, occupancies, priceMode, currencyContext, mode, itemBundleDataByDate])

  // Find the cheapest month for highlighting
  const cheapestMonthPrice = useMemo(() => {
    const prices = months.filter(m => m.price > 0).map(m => m.price)
    return prices.length > 0 ? Math.min(...prices) : 0
  }, [months])

  // Generate all date items for ALL months (lightweight - just dates)
  const allDateItems = useMemo(() => {
    const items: Array<DateItem> = []

    // Loop through all months
    Object.entries(dateRangesByMonth).forEach(([monthName, dateRanges]) => {
      // Skip months with no date entries
      if (dateRanges.length === 0) return

      dateRanges.forEach(({ departureDate, returnDate, checkIn, checkOut }) => {
        items.push({
          id: `${checkIn}-${nights}n`,
          checkIn: checkIn,
          checkOut: checkOut,
          departDate: departureDate,
          returnDate: returnDate,
          month: monthName,
        })
      })
    })

    return items
  }, [dateRangesByMonth, nights])

  const handleMonthSelect = useCallback((month: string) => {
    onMonthChange(month)
  }, [onMonthChange])

  const handleOptionSelect = useCallback((option: TripOption) => {
    onDateSelect(option)
  }, [onDateSelect])

  // Render month carousel content (reusable)
  const monthCarousel = (
    <StyledCardCarousel
      gap={12}
      alignment="center"
      snap="start"
      gutterSize={0}
      gutterStyle="overflow"
    >
      {months.map((month) => {
        const isCheapest = month.price > 0 && month.price === cheapestMonthPrice
        const priceColour = luxPlusEnabled ? 'lux-plus' : undefined
        const showRewardSeal = monthRewardSeals[month.name]?.showRewardSeal ?? false
        return (
          <MonthTile
            key={month.name}
            onClick={() => handleMonthSelect(month.name)}
            className={clsx({
              selected: selectedMonth === month.name,
              cheapest: isCheapest,
            })}
          >
            <BodyText variant="small" colour="neutral-three">
              {month.year}
            </BodyText>
            <BodyText variant="large">{month.monthLabel}</BodyText>
            <BodyText variant="large" weight="bold" colour={priceColour}>
              {month.fetching && <Caption variant="small">
                {intl.formatMessage({
                  id: 'ahqwZJxNJlNqe4lst3xR',
                  defaultMessage: 'Loading...',
                  description: 'Loading state text for charter flight month price in the flights section',
                })}
              </Caption>}
              {!month.fetching && month.price > 0 && <FormatCurrency value={month.price} format="roundedDollar" />}
              {!month.fetching && !month.price && <Caption variant="small">
                {intl.formatMessage({
                  id: 'OlOfqYtNbU_4z3O0cdFc',
                  defaultMessage: 'Unavailable',
                  description: 'Text shown when no charter flights available for month',
                })}
              </Caption>}
            </BodyText>
            {isRewardSeatsEnabled && showRewardSeal && !month.fetching && (
              <Group direction="vertical" horizontalAlign="center">
                <ClassicRewardsSealIcon size={20} colour="highlight-primary-dark" aria-hidden />
              </Group>
            )}
          </MonthTile>
        )
      })}
    </StyledCardCarousel>
  )

  return (
    <CharterRewardAvailabilityContext.Provider value={charterRewardContextValue}>
      <CalendarContainer gap={4}>
        {/* Month Selector - Improved Carousel */}
        <ScrollableContainer gap={0}>
          <div>
            {monthCarousel}
          </div>

          <TripOptionsContainer direction="vertical" gap={0}>
            <MaldivesCharterTripOptionsTable
              currentMonthTripOptions={allDateItems}
              selectedMonth={selectedMonth}
              dateRangesByMonth={dateRangesByMonth}
              luxPlusEnabled={luxPlusEnabled}
              selectedOptionId={selectedOptionId}
              onOptionSelect={handleOptionSelect}
              nights={nights}
              rooms={occupancies || [{ adults: 2, children: 0 }]}
              fareClass={props.selectedFareClass ?? fareClass}
              priceMode={priceMode}
              itemBundleDataByDate={itemBundleDataByDate}
              hotelPriceByCabinAndDate={hotelPriceByCabinAndDate}
              rewardSeatsOnly={rewardSeatsOnly}
            />
            <BottomLinkContainer>
              <BodyText variant="medium" colour="neutral-two">
                <FormattedMessage
                  id="h0D72ZYbNrwG1DVf4SJL"
                  defaultMessage="<strong>Looking for specific dates?</strong> <link>Search all Maldives offers</link>"
                  description="Link at bottom of charter flight calendar to search all Maldives offers"
                  values={{
                    strong: (chunks) => <strong>{chunks}</strong>,
                    link: (chunks) => (
                      <TextLink
                        weight="regular"
                        target="_blank"
                        to={`/search?${buildSearchParamsForFilterPane({
                        originalSearch: '',
                        destinationName: Places.Maldives.name,
                        destinationId: Places.Maldives.id,
                        adults: 2,
                        children: 'none',
                      })}`}
                      >
                        {chunks}
                      </TextLink>
                    ),
                  }}
                />
              </BodyText>
            </BottomLinkContainer>
          </TripOptionsContainer>
        </ScrollableContainer>
      </CalendarContainer>
    </CharterRewardAvailabilityContext.Provider>
  )
}

export default MaldivesDirectSearchFlightCalendar
